import React from "react";
import { IResourceComponentsProps } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import { ColumnDef } from "@tanstack/react-table";
import {
  ScrollArea,
  Table,
  Pagination,
  Flex,
  LoadingOverlay,
  Anchor,
  Badge,
  Avatar,
  Text,
} from "@mantine/core";
import {
  List,
  DeleteButton,
  EditButton,
  ShowButton,
  EmailField,
} from "@refinedev/mantine";
import type { Header } from "@tanstack/react-table";
import { Select } from "@mantine/core";
import AppliedFilters from "components/common/list/AppliedFilters";
import Headers from "components/common/list/Headers";
import { Rows } from "components/common/list/Rows";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import type { TeaAddonProblem } from "entities/tea_addon_problem";
import { selectKeys } from "entities/tea_addon_problem";

export const SanityCheckersList: React.FC<IResourceComponentsProps> = () => {
  const columns = React.useMemo<ColumnDef<TeaAddonProblem>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "ID",
      },
      {
        id: "sanity_checker_id",
        accessorFn: (row) => row.sanity_checker?.id,
        header: "Sanity Checker ID",
        cell: ({ row }) => {
          const sanityChecker = row.original.sanity_checker;
          if (!sanityChecker) return <Text size="sm">-</Text>;
          return <Badge color="blue" variant="light">{sanityChecker.id}</Badge>;
        },
      },
      {
        id: "key",
        accessorKey: "key",
        header: "Key",
        cell: ({ getValue }) => {
          const key = getValue<TeaAddonProblem["key"]>();
          // Format the key by adding spaces before capital letters and capitalizing words
          const formattedKey = key
            .replace(/_/g, " ")
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .split(/\s+/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
          return <Badge color="red">{formattedKey}</Badge>;
        },
        meta: {
          filterElement: ({ header }: { header: Header<TeaAddonProblem, any> }) => {
            return (
              <div>
                <Select
                  placeholder="Key"
                  data={selectKeys}
                  value={(header.column.getFilterValue() as string) || null}
                  onChange={(value) => header.column.setFilterValue(value || undefined)}
                  clearable
                />
              </div>
            );
          },
          filterOperator: "eq",
        },
      },
      {
        id: "state",
        accessorKey: "state",
        header: "State",
        cell: ({ getValue }) => {
          const state = getValue<TeaAddonProblem["state"]>();
          const color = state === "fixed" ? "green" : state === "fixing" ? "yellow" : "orange";
          return <Badge color={color}>{state}</Badge>;
        },
        meta: {
          filterElement: ({ header }: { header: Header<TeaAddonProblem, any> }) => {
            const stateOptions = [
              { value: "pending", label: "Pending" },
              { value: "fixing", label: "Fixing" },
              { value: "fixed", label: "Fixed" },
            ];
            return (
              <div>
                <Select
                  placeholder="State"
                  data={stateOptions}
                  value={(header.column.getFilterValue() as string) || null}
                  onChange={(value) => header.column.setFilterValue(value || undefined)}
                  clearable
                />
              </div>
            );
          },
          filterOperator: "eq",
        },
      },
      {
        id: "user",
        accessorKey: "user",
        header: "User",
        cell: ({ getValue }) => {
          const user = getValue<TeaAddonProblem["user"]>();
          return (
            <Anchor component={Link} to={`/users/show/${user.id}`}>
              <Flex align="center" gap="xs">
                <Avatar src={user.image} size="xs" />
                <EmailField value={user.email} />
              </Flex>
            </Anchor>
          );
        },
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created At",
        cell: ({ row }) => {
          const createdAt = row.original.created_at;
          if (!createdAt) return <Text size="sm">-</Text>;
          return (
            <Text size="sm">
              {format(new Date(createdAt), "yyyy-MM-dd HH:mm:ss")}
            </Text>
          );
        },
      },
      {
        id: "actions",
        cell: function render({ row: { original: problem } }) {
          return (
            <Flex columnGap={4}>
              <ShowButton
                hideText
                recordItemId={problem.id}
                resource="sanity_checkers"
              />
              <EditButton
                hideText
                recordItemId={problem.id}
                resource="sanity_checkers"
              />
              <DeleteButton
                hideText
                recordItemId={problem.id}
                resource="tea_addon_problems"
              />
            </Flex>
          );
        },
        meta: {
          isPlaceholder: true,
          disableSort: true,
        },
      },
    ],
    [],
  );

  const {
    getHeaderGroups,
    getRowModel,
    setOptions,
    getFlatHeaders,
    refineCore: {
      setCurrent,
      pageCount,
      current,
      tableQueryResult: { isFetching },
    },
  } = useTable({
    columns,
    refineCoreProps: {
      resource: "tea_addon_problems",
      pagination: {
        pageSize: 50,
      },
    },
  });

  setOptions((prev) => ({
    ...prev,
    meta: {
      ...prev.meta,
    },
  }));

  return (
    <List canCreate={false} resource="sanity_checkers">
      <AppliedFilters<TeaAddonProblem> headers={getFlatHeaders()} />
      <ScrollArea>
        <Table highlightOnHover pos="relative">
          <LoadingOverlay visible={isFetching} />
          <Headers<TeaAddonProblem> headerGroups={getHeaderGroups()} />
          <Rows<TeaAddonProblem> rows={getRowModel().rows} />
        </Table>
      </ScrollArea>
      <br />
      <Pagination
        position="right"
        total={pageCount}
        page={current}
        onChange={setCurrent}
      />
    </List>
  );
};
